// Record Payment Wizard — the wizard's own curated copy home. Seeded from
// the Phase A vocabulary catalogs where apt, but deliberately SEPARATE from
// the test-closed trigger/obligation vocabulary (waterfalls/triggerCodes.ts
// and its backend twin): this file explains the RECORDING decision to a
// staff member, not the engine's line items. Engine facts referenced here
// (fee-first allocation, pay-ahead roll-forward, no early principal
// reduction) are the BLA-grounded behaviors of ServicingPaymentOrchestrator
// after the pay-ahead / period-split fixes.

import type { ServicingPaymentType } from "@/api/portfolioAdmin/servicingApi";

// --- Steps ---------------------------------------------------------------

export const RECORD_PAYMENT_STEPS = [
  { key: "loan", label: "Confirm loan" },
  { key: "type", label: "Payment type" },
  { key: "period", label: "Applies to" },
  { key: "amount", label: "Amount & date" },
  { key: "review", label: "Review & record" },
] as const;

// --- Step 1 — loan & servicer ---------------------------------------------

export function loanConfirmSentence(loanNumber: string, tpsName: string): string {
  return `This payment will be recorded against ${loanNumber} and processed with ${tpsName} as servicer.`;
}

export const NO_SERVICER_CAUTION =
  "No servicer is assigned to this loan yet. You can still record the payment — the allocation runs the same either way — but check the loan's Servicing Platform card if a TPS should be attached.";

// --- Step 2 — payment types (owner-ruled lineup) ---------------------------
// Voluntary prepayment is deliberately NOT offered — it charges a prepayment
// fee + yield-maintenance premium and retires the loan, which is the Payoff
// flow's job (the signpost card below). payoff / default_interest_accrual /
// seller_make_whole stay unoffered.

export interface RecordPaymentTypeOption {
  value: ServicingPaymentType;
  label: string;
  blurb: string;
  /** The full "what recording this does" explainer shown on selection. */
  facts: string;
  /** Consequence warning rendered in warning tone when present. */
  warning?: string;
  /** Fee-only event — amount is never prefilled from the schedule. */
  isFeeEvent?: boolean;
}

export const RECORD_PAYMENT_TYPE_OPTIONS: ReadonlyArray<RecordPaymentTypeOption> = [
  {
    value: "monthly_p_and_i",
    label: "Monthly P&I",
    blurb: "The scheduled monthly principal & interest payment.",
    facts:
      "Most borrower checks bundle the regular payment with any assessed late or NSF fees. Record the full check as ONE Monthly P&I payment — the allocation automatically pays approved fees first (returned-payment fees to the servicer, late fees to Phoenix per BLA §2.8/§17.1/§2.10) and applies the rest to the scheduled payment. Only use the fee types below for a payment that is ONLY a fee.",
  },
  {
    value: "balloon_payment",
    label: "Balloon payment",
    blurb: "The final balloon installment on the note's schedule.",
    facts:
      "Records the scheduled balloon installment through the same allocation as a monthly payment.",
    warning:
      "A balloon payment recorded here does NOT close out the loan. If the borrower is paying off in full — including fees — use the Payoff flow instead.",
  },
  {
    value: "bmsp_extension_monthly_payment",
    label: "BMSP extension payment",
    blurb: "The monthly payment under an extended balloon — not the extension fee itself.",
    facts:
      "Use for the recurring monthly payment while the loan is in a balloon-maturity extension. The extension fee is assessed separately — never record it here.",
  },
  {
    value: "late_fee_event",
    label: "Late fee",
    blurb: "A fee-only payment covering an assessed late fee.",
    facts:
      "Paid to Phoenix. Use only for a payment that is ONLY a fee — a bundled check belongs under Monthly P&I, which pays approved fees first automatically.",
    warning:
      "Recording more than the assessed fee creates a new fee on the spot.",
    isFeeEvent: true,
  },
  {
    value: "nsf_event",
    label: "NSF / returned payment",
    blurb: "A fee-only payment covering a returned-payment (NSF) fee.",
    facts:
      "Usually recorded automatically when a payment is reversed. Only record manually when collecting an NSF fee outside a reversal.",
    isFeeEvent: true,
  },
];

export const PAYOFF_SIGNPOST = {
  title: "Paying off early?",
  body: "Voluntary prepayments charge a prepayment fee and yield-maintenance premium and retire the loan — use the Payoff flow (the “Record payoff” button on the Payment Tracking tab).",
} as const;

// --- Step 3 — period warnings ----------------------------------------------

export function duplicatePeriodWarning(amount: string, date: string): string {
  return `This period already has a recorded payment of ${amount} on ${date} — is this an additional payment?`;
}

export function arrearsWarning(oldestUnpaidLabel: string): string {
  return `${oldestUnpaidLabel} is still unpaid — payments normally apply to the oldest open period first.`;
}

export const DERIVE_FROM_DATE_LABEL = "Derive from received date";

// --- Step 3 — the boarding first-payment period -------------------------------
// Period 0 is settled at boarding by first-payment recording (100% to Phoenix,
// outside the waterfall). The server REFUSES a monthly P&I run against it, so
// the wizard must neither default to it nor let a monthly entry land on it
// silently. The suffix marks it in the picker; the notice explains a deliberate
// selection; the success line replaces the generic "already recorded" for a run
// the server refused.

export const BOARDING_PERIOD_OPTION_SUFFIX = " — recorded at boarding";

export const BOARDING_PERIOD_MONTHLY_BLOCKED =
  "This period was already settled by the borrower's first payment at boarding (recorded 100% to Phoenix, outside the waterfall). A monthly P&I entry against it is refused by the server and records nothing — pick the next open period instead.";

export const BOARDING_PERIOD_RECORD_BLOCKED_ERROR =
  "This period was settled at boarding — a monthly P&I payment can't be recorded against it. Choose the next open period.";

// The ordinary caption claims the default IS the oldest period still owed. When
// the oldest still-owed period is the boarding period we deliberately skip past
// it, so that sentence stops being true and has to say what it actually did.
export const BOARDING_PERIOD_DEFAULT_SKIPPED_HINT =
  "The oldest period still owed is the first-payment period, which was settled at boarding — this defaults to the next one after it instead. Change it only to apply this payment to a different period.";

export const BOARDING_FIRST_PAYMENT_REFUSED =
  "Nothing was recorded. This period was already settled by the borrower's first payment at boarding, so a monthly P&I entry against it is refused — recording it again would count the period twice. Choose the next open period instead.";

// --- Step 4 — date guards ----------------------------------------------------

export const FUTURE_DATE_ERROR =
  "The received date can't be in the future — a receipt records cash already received.";

export function beforeFirstPaymentWarning(firstPaymentLabel: string): string {
  return `This date is before the loan's first payment date (${firstPaymentLabel}) — double-check it.`;
}

export const STALE_DATE_WARNING =
  "This date is more than 2 months in the past — double-check it before recording.";

export function derivedPeriodMismatchWarning(
  derivedLabel: string,
  selectedLabel: string,
): string {
  return `A payment received on this date would normally derive to the ${derivedLabel} period, but you selected ${selectedLabel}. The selected period wins — make sure that's intended.`;
}

// --- Step 4 — amount-mismatch acknowledgments (owner-ruled: explain +
// REQUIRED acknowledgment + say exactly what happens next) --------------------

export const PARTIAL_EXPLANATION =
  "The shortfall stays on the books — the next payment pays the oldest unpaid amounts first, and this period will show as Partial. Fees and Phoenix/servicer shares are paid first, so the shortfall reduces what the seller receives this month. No late fee is charged automatically by recording this payment — late fees are assessed separately (5% of the overdue amount after the grace period, per the note terms) and require staff approval.";

export const PARTIAL_ACK_LABEL = "I understand this is a partial payment.";

export function payAheadExplanation(preview: string): string {
  return `The excess pays future installments early — it is recorded and distributed now, never held, and it does not reduce principal ahead of schedule (BLA §2.5/Exh. G). ${preview}`;
}

export const PAY_AHEAD_ACK_LABEL =
  "I understand this pays future installments early.";

/** Shown on the REVIEW step when the amount stopped matching what the borrower
 *  owes after the acknowledgment step was passed — a ledger refetch landing
 *  while the wizard is open, or a bill regenerating under a cleared payment.
 *  The acknowledgment checkboxes live on the amount step, so the only way to
 *  satisfy the gate is to go back; say so rather than leaving a disabled
 *  button with no explanation. */
export const REVIEW_AMOUNT_RECHECK_NOTICE =
  "What this payment is measured against changed while you were reviewing it, so the difference below is no longer one anyone has confirmed. Go back to Amount & date to check the figure and acknowledge it before recording.";

export const FAR_ABOVE_WARNING =
  "This amount exceeds the loan's remaining scheduled payments — the server will reject it and direct you to the Payoff flow. If the borrower is paying off the loan, cancel and use Record payoff instead.";

/** Shown whenever the borrower has approved fee money outstanding, so the
 *  expected amount is visibly the sum of two things rather than one unexplained
 *  larger number. The schedule figure and the fee figure are named separately
 *  because they come from different places — the amortization schedule and the
 *  Fee & Obligation ledger — and staff need to be able to reconcile the total
 *  against either one. */
export function expectedWithFeesBreakdown(scheduled: string, fees: string): string {
  return `${scheduled} scheduled + ${fees} approved fees.`;
}

/** The consequence of recording only the scheduled amount, stated where the
 *  amount is entered rather than after the fact. This is what actually happened
 *  on the loans that surfaced this: the fee is senior to the seller in WF1, so
 *  it is paid in full out of a payment that did not cover it and the seller's
 *  principal leg absorbs the whole shortfall. */
export const OUTSTANDING_FEES_EXPLANATION =
  "Fees are collected ahead of the seller in the waterfall. Recording only the scheduled amount still pays them in full — out of the seller's share — and leaves the difference owed as arrears.";

// --- Prefill (SP-2 cleared-buyer-payment flow) -------------------------------

export function prefillMismatchWarning(bill: string, cleared: string): string {
  return `This doesn't match the bill (${bill}) or the cleared payment (${cleared}) — double-check before recording.`;
}

export const LOCKED_REFERENCE_NOTE =
  "Set automatically so this payment can't be recorded twice";

export const AUTO_REFERENCE_NOTE =
  "A reference is generated automatically so a double-click or retry can't record this payment twice.";

// --- Error mapping ------------------------------------------------------------

/** Strip .NET exception plumbing from a server validation message so staff
 *  never see "(Parameter 'request')" and friends. */
export function friendlyServerError(message: string): string {
  const stripped = message.replace(/\s*\(Parameter\s+'[^']*'\)\s*/g, " ").trim();
  return stripped.length > 0 ? stripped : "The payment could not be recorded.";
}
